import React, { useEffect, useState } from 'react';
import {
  Card,
  List,
  Typography,
  Space,
  Button,
  App,
  Pagination,
  Select,
  Modal,
  Form,
  Input,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useTranslation } from 'react-i18next';

const { Title, Text, Paragraph } = Typography;

interface UserReviewItem {
  id: number;
  brandId: number;
  brandName?: string;
  content: string;
  pros?: string[];
  cons?: string[];
  createdAt: string;
}

interface ApiPagination {
  limit: number;
  offset?: number;
  total: number;
  page?: number;
  totalPages?: number;
}

interface ApiListResponse<T> {
  success: boolean;
  data: T[];
  pagination?: ApiPagination;
}

const MyReviewsPage: React.FC = () => {
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<UserReviewItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [sort, setSort] = useState<'latest' | 'oldest'>('latest');

  const [editOpen, setEditOpen] = useState(false);
  const [editForm] = Form.useForm();
  const [editingId, setEditingId] = useState<number | null>(null);

  const loadData = async (p = page) => {
    setLoading(true);
    try {
      const res = await apiClient.get<ApiListResponse<UserReviewItem>>(
        `/v1/users/reviews?page=${p}&limit=${pageSize}&sort=${sort}`
      );
      setItems(res.data || []);
      setTotal(res.pagination?.total || res.data?.length || 0);
    } catch (err: unknown) {
      message.error(t('myreviews.loading_failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(1);
  }, [sort]);

  const onEdit = (rv: UserReviewItem) => {
    setEditingId(rv.id);
    editForm.setFieldsValue({
      content: rv.content,
      pros: (rv.pros || []).join('\n'),
      cons: (rv.cons || []).join('\n'),
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    try {
      const values = await editForm.validateFields();
      await apiClient.put(`/reviews/${editingId}`, {
        content: values.content,
        pros: values.pros
          ? values.pros.split('\n').filter((x: string) => x.trim())
          : [],
        cons: values.cons
          ? values.cons.split('\n').filter((x: string) => x.trim())
          : [],
      });
      message.success(t('common.success', { defaultValue: 'Success' }));
      setEditOpen(false);
      setEditingId(null);
      editForm.resetFields();
      loadData(page);
    } catch (e: unknown) {
      // 校验错误忽略
      if (
        typeof e === 'object' &&
        e !== null &&
        'errorFields' in e &&
        Array.isArray((e as { errorFields?: unknown }).errorFields)
      )
        return;
      message.error(t('common.error'));
    }
  };

  const onDelete = (id: number) => {
    modal.confirm({
      title: t('myreviews.confirm_delete_title'),
      content: t('myreviews.confirm_delete_content'),
      onOk: async () => {
        try {
          await apiClient.delete(`/reviews/${id}`);
          message.success(t('myreviews.delete_success'));
          loadData(page);
        } catch (e: unknown) {
          message.error(t('myreviews.delete_failed'));
        }
      },
    });
  };

  const onPageChange = (p: number) => {
    setPage(p);
    loadData(p);
  };

  return (
    <div style={{ padding: '24px', maxWidth: 900, margin: '0 auto' }}>
      <Title level={2} style={{ marginBottom: 16 }}>
        {t('myreviews.title')}
      </Title>
      <Card
        extra={
          <Space>
            <span>{t('myreviews.sort_label')}</span>
            <Select
              size="small"
              value={sort}
              options={[
                { value: 'latest', label: t('myreviews.sort_latest') },
                { value: 'oldest', label: t('myreviews.sort_oldest') },
              ]}
              onChange={v => setSort(v)}
              style={{ width: 100 }}
            />
          </Space>
        }
      >
        <List
          loading={loading}
          dataSource={items}
          locale={{ emptyText: t('myreviews.empty_title') }}
          renderItem={rv => (
            <List.Item
              key={rv.id}
              actions={[
                <Button
                  key="view"
                  type="link"
                  onClick={() => navigate(`/brands/${rv.brandId}`)}
                >
                  {t('myreviews.view_brand')}
                </Button>,
                <Button key="edit" type="link" onClick={() => onEdit(rv)}>
                  {t('myreviews.edit')}
                </Button>,
                <Button
                  key="delete"
                  type="link"
                  danger
                  onClick={() => onDelete(rv.id)}
                >
                  {t('myreviews.delete')}
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={
                  <Space>
                    <Text strong>{rv.brandName || `#${rv.brandId}`}</Text>
                    <Text type="secondary">
                      {new Date(rv.createdAt).toLocaleString()}
                    </Text>
                  </Space>
                }
                description={
                  <div>
                    <Paragraph style={{ marginBottom: 8 }}>
                      {rv.content}
                    </Paragraph>
                    {rv.pros && rv.pros.length > 0 && (
                      <div style={{ fontSize: 12, marginBottom: 4 }}>
                        <Text strong style={{ color: '#52c41a' }}>
                          {t('myreviews.pros_label')}：
                        </Text>
                        <Text>{rv.pros.join('、')}</Text>
                      </div>
                    )}
                    {rv.cons && rv.cons.length > 0 && (
                      <div style={{ fontSize: 12 }}>
                        <Text strong style={{ color: '#ff4d4f' }}>
                          {t('myreviews.cons_label')}：
                        </Text>
                        <Text>{rv.cons.join('、')}</Text>
                      </div>
                    )}
                  </div>
                }
              />
            </List.Item>
          )}
        />
        {total > pageSize && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Pagination
              current={page}
              pageSize={pageSize}
              total={total}
              onChange={onPageChange}
              showSizeChanger={false}
            />
          </div>
        )}
      </Card>

      <Modal
        title={t('myreviews.edit_modal_title')}
        open={editOpen}
        onCancel={() => {
          setEditOpen(false);
          setEditingId(null);
          editForm.resetFields();
        }}
        onOk={submitEdit}
        okText={t('myreviews.save')}
      >
        <Form layout="vertical" form={editForm}>
          <Form.Item
            name="content"
            label={t('myreviews.form_content_label')}
            rules={[
              { required: true, message: t('myreviews.content_required') },
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder={t('myreviews.content_placeholder')}
            />
          </Form.Item>
          <Form.Item name="pros" label={t('myreviews.pros_label')}>
            <Input.TextArea
              rows={3}
              placeholder={t('myreviews.pros_placeholder')}
            />
          </Form.Item>
          <Form.Item name="cons" label={t('myreviews.cons_label')}>
            <Input.TextArea
              rows={3}
              placeholder={t('myreviews.cons_placeholder')}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MyReviewsPage;
